import { useMemo, useState, useContext, useEffect } from 'react';
import { MdOutlineArrowForwardIos } from 'react-icons/md';
import { FaEye, FaPencilAlt } from 'react-icons/fa';
import { FiChevronDown, FiChevronRight, FiDownload } from 'react-icons/fi';
import { PiRecordFill } from 'react-icons/pi';
import AuthContext from '../../context/AuthContext.js';
import api from '../../services/AxiosConnection.js';
import DownloadPDF from '../../utils/DownloadPDF.js';


const TempLogTable = ({ temperatureLogs, trainId, onUpdate }) => {
  const [selectedLog, setSelectedLog] = useState(null);
  const [checkedTs1, setCheckedTs1] = useState('');
  const [checkedTs2, setCheckedTs2] = useState('');
  const [checkedTs3, setCheckedTs3] = useState('');
  const [checkedTs4, setCheckedTs4] = useState('');
  const [checkedTs5, setCheckedTs5] = useState('');
  const [checkedTs6, setCheckedTs6] = useState('');
  const [recommendedAction, setRecommendedAction] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [categoryOfDefect, setCategoryOfDefect] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [filterOption, setFilterOption] = useState('All');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const { selectedSite, user, selectedSiteName } = useContext(AuthContext);

  const noOfTempSensors = temperatureLogs.length > 0 ? temperatureLogs[0].Nooftempsens || 2 : 2;

  const formatTemperature = (temp) => {
    return temp !== undefined && temp !== null 
      ? parseFloat(temp).toFixed(2)
      : "N/A";
  };

  const calculateDifference = (a, b) => {
    if ([a, b].some(v => v === null || v === undefined)) return "N/A";
    return Math.abs(a - b).toFixed(2);
  };

  const calculateAverage = (a, b) => {
    if ([a, b].some(v => v === null || v === undefined)) return "N/A";
    return ((a + b) / 2).toFixed(2);
  };

  const formatValue = (value) => {
    return value !== undefined && value !== null 
      ? parseFloat(value).toFixed(2)
      : "";
  };

  const getTempStatusIcon = (alarm, warning) => {
    if (alarm) {
      return <PiRecordFill className="ml-1 text-red-500" />;
    } else if (warning) {
      return <PiRecordFill className="ml-1 text-yellow-500" />;
    }
    return null;
  };

  const coachPositionsAndTypes = useMemo(() => {
    const positions = [];
    const types = [];
    let locoCount = 0;
    let wag12Count = 0;
    let normalCount = 1;
    let skip = 0;

    for (let i = 0; i < temperatureLogs.length; i++) {
      if (skip > 0) {
        positions[i] = '';
        types[i] = '';
        skip--;
        continue;
      }

      if (temperatureLogs[i].CoachPosition === 'LOCO') {
        locoCount++;
        positions[i] = `LOCO ${locoCount}`;
        types[i] = temperatureLogs[i].Description || 'N/A';
        skip = 5; // LOCO has 6 axles
      } else if (temperatureLogs[i].CoachPosition === 'WAG12') {
        wag12Count++;
        if (wag12Count === 1) {
          positions[i] = `WAG12 1.1`;
        } else if (wag12Count === 2) {
          positions[i] = `WAG12 1.2`;
        } else {
          positions[i] = `WAG12 ${Math.ceil((wag12Count - 2) / 2) + 1}.${(wag12Count - 2) % 2 === 1 ? 1 : 2}`;
        }
        types[i] = temperatureLogs[i].Description || 'N/A';
        skip = 3; // WAG12 has 4 axles
      } else {
        positions[i] = temperatureLogs[i].CoachPosition;
        types[i] = temperatureLogs[i].Description || 'N/A';
        skip = 3; // Normal coaches have 4 axles
      }
    }
    return { positions, types };
  }, [temperatureLogs]);

  const coachNumbers = useMemo(() => {
    const numbers = [];
    let locoCount = 0;
    let wag12Count = 0;
    let normalCount = 1;
    let skip = 0;

    for (let i = 0; i < temperatureLogs.length; i++) {
      if (skip > 0) {
        numbers[i] = '';
        skip--;
        continue;
      }

      if (temperatureLogs[i].CoachPosition === 'LOCO') {
        locoCount++;
        numbers[i] = temperatureLogs[i].CoachNo || 'N/A';
        skip = 5; // LOCO has 6 axles
      } else if (temperatureLogs[i].CoachPosition === 'WAG12') {
        wag12Count++;
        numbers[i] = temperatureLogs[i].CoachNo || 'N/A';
        skip = 3; // WAG12 has 4 axles
      } else {
        numbers[i] = temperatureLogs[i].CoachNo || 'N/A';
        normalCount++;
        skip = 3; // Normal coaches have 4 axles
      }
    }
    return numbers;
  }, [temperatureLogs]);

  const coachRFIDs = useMemo(() => {
    const rfids = [];
    let locoCount = 0;
    let wag12Count = 0;
    let normalCount = 1;
    let skip = 0;

    for (let i = 0; i < temperatureLogs.length; i++) {
      if (skip > 0) {
        rfids[i] = '';
        skip--;
        continue;
      }

      if (temperatureLogs[i].CoachPosition === 'LOCO') {
        locoCount++;
        rfids[i] = temperatureLogs[i].hex_decoded_id || 'N/A';
        skip = 5; // LOCO has 6 axles
      } else if (temperatureLogs[i].CoachPosition === 'WAG12') {
        wag12Count++;
        rfids[i] = temperatureLogs[i].hex_decoded_id || 'N/A';
        skip = 3; // WAG12 has 4 axles
      } else {
        rfids[i] = temperatureLogs[i].hex_decoded_id || 'N/A';
        normalCount++;
        skip = 3; // Normal coaches have 4 axles
      }
    }
    return rfids;
  }, [temperatureLogs]);

  const hasUpdateData = (log) => {
    return (
      log.Checked_Ts1 !== null ||
      log.Checked_Ts2 !== null ||
      log.Checked_Ts3 !== null ||
      log.Checked_Ts4 !== null ||
      log.Checked_Ts5 !== null ||
      log.Checked_Ts6 !== null ||
      log.acknowledged_by !== null ||
      log.Recommended_Action !== null ||
      log.Action_Taken !== null ||
      log.Category_of_Defect !== null
    );
  };

  const closePopup = () => {
    setSelectedLog(null);
    setCheckedTs1('');
    setCheckedTs2('');
    setCheckedTs3('');
    setCheckedTs4('');
    setCheckedTs5('');
    setCheckedTs6('');
    setRecommendedAction('');
    setActionTaken('');
    setCategoryOfDefect('');
    setIsEditable(false);
  };

  const handleViewClick = (log) => {
    setSelectedLog(log);
    setCheckedTs1(formatValue(log.Checked_Ts1));
    setCheckedTs2(formatValue(log.Checked_Ts2));
    setCheckedTs3(formatValue(log.Checked_Ts3));
    setCheckedTs4(formatValue(log.Checked_Ts4));
    setCheckedTs5(formatValue(log.Checked_Ts5));
    setCheckedTs6(formatValue(log.Checked_Ts6));
    setRecommendedAction(log.Recommended_Action || '');
    setActionTaken(log.Action_Taken || '');
    setCategoryOfDefect(log.Category_of_Defect || '');
    setIsEditable(false);
  };

  const handleEditClick = (log) => {
    setSelectedLog(log);
    setCheckedTs1(formatValue(log.Checked_Ts1));
    setCheckedTs2(formatValue(log.Checked_Ts2));
    setCheckedTs3(formatValue(log.Checked_Ts3));
    setCheckedTs4(formatValue(log.Checked_Ts4));
    setCheckedTs5(formatValue(log.Checked_Ts5));
    setCheckedTs6(formatValue(log.Checked_Ts6));
    setRecommendedAction(log.Recommended_Action || '');
    setActionTaken(log.Action_Taken || '');
    setCategoryOfDefect(log.Category_of_Defect || '');
    setIsEditable(true);
  };

  const handleUpdate = async () => {
    if (!selectedLog || !selectedSite || !user) return;

    const formData = {
      siteDb: selectedSite,
      tempLogId: selectedLog.TempLogID,
      checkedTs1: parseFloat(checkedTs1) || null,
      checkedTs2: parseFloat(checkedTs2) || null,
      checkedTs3: parseFloat(checkedTs3) || null,
      checkedTs4: parseFloat(checkedTs4) || null,
      checkedTs5: parseFloat(checkedTs5) || null,
      checkedTs6: parseFloat(checkedTs6) || null,
      acknowledgedBy: user.username,
      recommendedAction,
      actionTaken,
      categoryOfDefect,
    };

    setIsUpdating(true);
    try {
      const response = await api.put('/main-view/updateTemperatureLogDetails', formData);
      // console.log('Temperature log updated successfully:', response.data);
      onUpdate();
      closePopup();
    } catch (error) {
      console.error('Error updating temperature log:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDownload = () => {
    const trainDetails = { TrainId: trainId };
    DownloadPDF(selectedSite, selectedSiteName, trainDetails, setIsLoading);
  };

  useEffect(() => {
    // console.log('TempLogTable re-rendered with temperatureLogs:', temperatureLogs);
  }, [temperatureLogs]);

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  const commonFields = [
    { header: 'Axle No', key: 'AxleNo', render: (log) => log.AxleNo, className: 'px-6 py-2' },
    { header: 'Coach Position', key: 'CoachPosition', render: (log, index) => coachPositionsAndTypes.positions[index], className: 'px-2 py-2' },
    { header: 'Coach Type', key: 'CoachType', render: (log, index) => coachPositionsAndTypes.types[index], className: 'px-2 py-2' },
    { header: 'Coach Number', key: 'CoachNo', render: (log, index) => coachNumbers[index], className: 'px-6 py-2' },
    { 
      header: 'Coach RFID', 
      key: 'CoachRFID', 
      render: (log, index) => {
        const rfid = coachRFIDs[index];
        if (rfid === "N/A" || !rfid) {
          return <div>{rfid}</div>;
        }
        return (
          <div 
            className="relative"
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {rfid}
            {hoveredIndex === index && (
              <div className="absolute z-[3] bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-96 left-1/2 transform -translate-x-1/2 mt-2 bg-gradient-to-br from-blue-50 to-gray-50 transition-transform duration-200">
                <h4 className="text-sm font-bold text-gray-800 mb-3">RFID Details</h4>
                <div className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-2 text-xs text-gray-700">
                  <span className="font-semibold text-gray-900 text-left">Hex Decoded ID:</span>
                  <span className="break-all">{log.hex_decoded_id || "N/A"}</span>
                  <span className="font-semibold text-gray-900 text-left">Reader ID:</span>
                  <span>{log.reader_id || "N/A"}</span>
                  <span className="font-semibold text-gray-900 text-left">GS1 India:</span>
                  <span>{log.GS1_India || "N/A"}</span>
                  <span className="font-semibold text-gray-900 text-left">Version No:</span>
                  <span>{log.version_no || "N/A"}</span>
                  <span className="font-semibold text-gray-900 text-left">Vehicle Side:</span>
                  <span>{log.vehicle_side || "N/A"}</span>
                  <span className="font-semibold text-gray-900 text-left">Asset Type:</span>
                  <span>{log.asset_type || "N/A"}</span>
                  <span className="font-semibold text-gray-900 text-left">Manufacturing Year:</span>
                  <span>{log.manufacturing_yr || "N/A"}</span>
                  <span className="font-semibold text-gray-900 text-left">Owner Zone:</span>
                  <span>{log.owner_zone || "N/A"}</span>
                  <span className="font-semibold text-gray-900 text-left">Transportation Code:</span>
                  <span>{log.transportation_code || "N/A"}</span>
                  <span className="font-semibold text-gray-900 text-left">Serial No:</span>
                  <span>{log.serial_no || "N/A"}</span>
                </div>
              </div>
            )}
          </div>
        );
      }, 
      className: 'px-6 py-2' 
    },
  ];

  const axleFields = [
    { header: 'LH Axle (°C)', key: 'Ts1', render: (log) => (
      <span className="inline-flex items-center">{formatTemperature(log.Ts1)}{getTempStatusIcon(log.Ts1Alarm, log.Ts1Warning)}</span>
    ), className: 'px-6 py-2' },
    { header: 'RH Axle (°C)', key: 'Ts2', render: (log) => (
      <span className="inline-flex items-center">{formatTemperature(log.Ts2)}{getTempStatusIcon(log.Ts2Alarm, log.Ts2Warning)}</span>
    ), className: 'px-6 py-2' },
    { header: 'Axle Temp Diff (°C)', key: 'AxleTempDiff', render: (log) => calculateDifference(log.Ts1, log.Ts2), className: 'px-6 py-2' },
    { header: 'Axle Avg Temp (°C)', key: 'AxleAvgTemp', render: (log) => calculateAverage(log.Ts1, log.Ts2), className: 'px-6 py-2' },
  ];

  const wheelFields = [
    { header: 'LH Wheel (°C)', key: 'Ts3', render: (log) => (
      <span className="inline-flex items-center">{formatTemperature(log.Ts3)}{getTempStatusIcon(log.Ts3Alarm, log.Ts3Warning)}</span>
    ), className: 'px-6 py-2' },
    { header: 'RH Wheel (°C)', key: 'Ts4', render: (log) => (
      <span className="inline-flex items-center">{formatTemperature(log.Ts4)}{getTempStatusIcon(log.Ts4Alarm, log.Ts4Warning)}</span>
    ), className: 'px-6 py-2' },
    { header: 'Wheel Temp Diff (°C)', key: 'WheelTempDiff', render: (log) => calculateDifference(log.Ts3, log.Ts4), className: 'px-6 py-2' },
    { header: 'Wheel Avg Temp (°C)', key: 'WheelAvgTemp', render: (log) => calculateAverage(log.Ts3, log.Ts4), className: 'px-6 py-2' },
  ];

  const brakeDiscFields = [
    { header: 'LH Brake Disc (°C)', key: 'Ts5', render: (log) => (
      <span className="inline-flex items-center">{formatTemperature(log.Ts5)}{getTempStatusIcon(log.Ts5Alarm, log.Ts5Warning)}</span>
    ), className: 'px-6 py-2' },
    { header: 'RH Brake Disc (°C)', key: 'Ts6', render: (log) => (
      <span className="inline-flex items-center">{formatTemperature(log.Ts6)}{getTempStatusIcon(log.Ts6Alarm, log.Ts6Warning)}</span>
    ), className: 'px-6 py-2' },
    { header: 'Brake Disc Temp Diff (°C)', key: 'BrakeDiscTempDiff', render: (log) => calculateDifference(log.Ts5, log.Ts6), className: 'px-6 py-2' },
    { header: 'Brake Disc Avg Temp (°C)', key: 'BrakeDiscAvgTemp', render: (log) => calculateAverage(log.Ts5, log.Ts6), className: 'px-6 py-2' },
  ];

  const actionField = [
    { header: 'User Feedback', key: 'UserFeedback', render: (log) => (
      hasUpdateData(log) ? (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => handleViewClick(log)} className="bg-gray-200 rounded-full p-1 text-blue-600 hover:text-blue-800">
            <FaEye />
          </button>
          <button onClick={() => handleEditClick(log)} className="bg-gray-200 rounded-full p-1 text-blue-600 hover:text-blue-800">
            <FaPencilAlt />
          </button>
        </div>
      ) : (
        <button onClick={() => handleEditClick(log)} className="bg-gray-200 rounded-full p-1 text-blue-600 hover:text-blue-800">
          <MdOutlineArrowForwardIos />
        </button>
      )
    ), className: 'px-10 py-2' },
  ];

  const columnConfigs = {
    'All': [
      ...commonFields,
      ...(noOfTempSensors >= 2 ? axleFields : []),
      ...(noOfTempSensors >= 4 ? wheelFields : []),
      ...(noOfTempSensors >= 6 ? brakeDiscFields : []),
      { header: 'Axle Speed', key: 'AxelSpeed', render: (log) => formatTemperature(log.AxelSpeed), className: 'px-6 py-2' },
      ...actionField,
    ],
    'Axle Temp': noOfTempSensors >= 2 ? [...commonFields, ...axleFields] : [...commonFields],
    'Wheel Temp': noOfTempSensors >= 4 ? [...commonFields, ...wheelFields] : [...commonFields],
    'Brake Disc Temp': noOfTempSensors >= 6 ? [...commonFields, ...brakeDiscFields] : [...commonFields],
    'Avg Temp': [
      ...commonFields,
      ...(noOfTempSensors >= 2 ? [{ header: 'Avg Axle Temp (°C)', key: 'AxleAvgTemp', render: (log) => calculateAverage(log.Ts1, log.Ts2), className: 'px-6 py-2' }] : []),
      ...(noOfTempSensors >= 4 ? [{ header: 'Avg Wheel Temp (°C)', key: 'WheelAvgTemp', render: (log) => calculateAverage(log.Ts3, log.Ts4), className: 'px-6 py-2' }] : []),
      ...(noOfTempSensors >= 6 ? [{ header: 'Avg Brake Disc Temp (°C)', key: 'BrakeDiscAvgTemp', render: (log) => calculateAverage(log.Ts5, log.Ts6), className: 'px-6 py-2' }] : []),
    ],
    'Temp Diff': [
      ...commonFields,
      ...(noOfTempSensors >= 2 ? [{ header: 'Axle Temp Diff (°C)', key: 'AxleTempDiff', render: (log) => calculateDifference(log.Ts1, log.Ts2), className: 'px-6 py-2' }] : []),
      ...(noOfTempSensors >= 4 ? [{ header: 'Wheel Temp Diff (°C)', key: 'WheelTempDiff', render: (log) => calculateDifference(log.Ts3, log.Ts4), className: 'px-6 py-2' }] : []),
      ...(noOfTempSensors >= 6 ? [{ header: 'Brake Disc Temp Diff (°C)', key: 'BrakeDiscTempDiff', render: (log) => calculateDifference(log.Ts5, log.Ts6), className: 'px-6 py-2' }] : []),
    ],
    'User Feedback': [...commonFields, ...actionField],
  };

  const commonButtons = ['All', 'Avg Temp', 'Temp Diff', 'User Feedback'];
  const availableFilterOptions = [
    ...commonButtons,
    ...(noOfTempSensors >= 2 ? ['Axle Temp'] : []),
    ...(noOfTempSensors >= 4 ? ['Wheel Temp'] : []),
    ...(noOfTempSensors >= 6 ? ['Brake Disc Temp'] : []),
  ];

  const selectedColumns = columnConfigs[filterOption] || columnConfigs['All'];

  return (
    <div className="bg-white rounded-lg shadow mt-4 w-full mx-auto overflow-hidden">
      {/* Mobile View: Horizontal Scroll for Buttons */}
      <div className="md:hidden p-4 border-b border-gray-300 flex overflow-x-auto gap-2 items-center min-h-[60px] scrollbar-custom">
        {availableFilterOptions.map((option) => (
          <button
            key={option}
            onClick={() => setFilterOption(option)}
            className={`px-4 py-2 text-sm rounded-md transition whitespace-nowrap ${
              filterOption === option
                ? 'bg-blue-900 text-white'
                : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
            }`}
          >
            {option}
          </button>
        ))}
        <button
          onClick={handleDownload}
          className="text-gray-700 hover:text-gray-900 transition flex-shrink-0"
          aria-label="Download"
          disabled={isLoading}
        >
          {isLoading ? (
            <svg
              className="animate-spin h-5 w-5 text-gray-700"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            <FiDownload size={20} />
          )}
        </button>
        <button
          onClick={toggleCollapse}
          className="text-gray-700 hover:text-gray-900 transition flex-shrink-0"
          aria-label="Toggle Table"
        >
          {isCollapsed ? <FiChevronRight size={20} /> : <FiChevronDown size={20} />}
        </button>
      </div>

      {/* Desktop View: Centered Buttons */}
      <div className="hidden md:flex p-4 border-b border-gray-300 justify-between items-center min-h-[60px]">
        <div className="flex justify-center gap-2 flex-1">
          {availableFilterOptions.map((option) => (
            <button
              key={option}
              onClick={() => setFilterOption(option)}
              className={`px-4 py-2 text-sm rounded-md transition ${
                filterOption === option
                  ? 'bg-blue-900 text-white'
                  : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownload}
            className="text-gray-700 hover:text-gray-900 transition"
            aria-label="Download"
            disabled={isLoading}
          >
            {isLoading ? (
              <svg
                className="animate-spin h-5 w-5 text-gray-700"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              <FiDownload size={20} />
            )}
          </button>
          <button
            onClick={toggleCollapse}
            className="text-gray-700 hover:text-gray-900 transition"
            aria-label="Toggle Table"
          >
            {isCollapsed ? <FiChevronRight size={20} /> : <FiChevronDown size={20} />}
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <>
          <div className="overflow-x-auto overflow-y-auto max-h-[475px] scrollbar-custom rounded-b-lg">
            <table className="w-full">
              <thead className="sticky top-0 z-[4] bg-blue-900 text-white">
                <tr>
                  {selectedColumns.map((col, index) => (
                    <th
                      key={index}
                      className={`${col.className} text-center text-xs border-b border-gray-300`}
                    >
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {temperatureLogs.length > 0 ? (
                  temperatureLogs.map((log, index) => (
                    <tr
                      key={log.TempLogID}
                      className={`hover:bg-gray-300 ${coachPositionsAndTypes.positions[index] ? 'bg-gray-100' : ''}`}
                    >
                      {selectedColumns.map((col, colIndex) => (
                        <td
                          key={colIndex}
                          className={`${col.className} text-center text-xs`}
                        >
                          {col.render(log, index)}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={selectedColumns.length}
                      className="px-6 py-2 text-center text-xs text-black"
                    >
                      No Data
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {selectedLog && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
              <div className="p-4 bg-white rounded-xl shadow-2xl w-11/12 max-w-4xl relative">
                <button
                  className="absolute top-2 right-2 text-gray-700 hover:text-red-600 text-2xl font-bold"
                  onClick={closePopup}
                >
                  ×
                </button>
                <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">User Input / Feedback Details</h2>
                <div className="grid grid-cols-5 gap-4 text-sm">
                  <div className="col-span-3 grid grid-cols-3 gap-2 items-center">
                    <div className="text-gray-700">
                      Axle Temp. LH:{' '}
                      <span className="inline-flex items-center font-semibold">
                        {formatValue(selectedLog?.Ts1)}
                        {getTempStatusIcon(selectedLog?.Ts1Alarm, selectedLog?.Ts1Warning)}
                      </span>
                    </div>
                    <div className="text-gray-700">Checked Axle Temp. LH:</div>
                    <input
                      type="text"
                      className="border border-gray-300 rounded-md p-1 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 disabled:bg-gray-100 w-full"
                      value={checkedTs1}
                      onChange={(e) => setCheckedTs1(e.target.value)}
                      disabled={!isEditable}
                    />
                    <div className="text-gray-700">
                      Axle Temp. RH:{' '}
                      <span className="inline-flex items-center font-semibold">
                        {formatValue(selectedLog?.Ts2)}
                        {getTempStatusIcon(selectedLog?.Ts2Alarm, selectedLog?.Ts2Warning)}
                      </span>
                    </div>
                    <div className="text-gray-700">Checked Axle Temp. RH:</div>
                    <input
                      type="text"
                      className="border border-gray-300 rounded-md p-1 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 disabled:bg-gray-100 w-full"
                      value={checkedTs2}
                      onChange={(e) => setCheckedTs2(e.target.value)}
                      disabled={!isEditable}
                    />
                    {noOfTempSensors >= 4 && (
                      <>
                        <div className="text-gray-700">
                          Wheel Temp. LH:{' '}
                          <span className="inline-flex items-center font-semibold">
                            {formatValue(selectedLog?.Ts3)}
                            {getTempStatusIcon(selectedLog?.Ts3Alarm, selectedLog?.Ts3Warning)}
                          </span>
                        </div>
                        <div className="text-gray-700">Checked Wheel Temp. LH:</div>
                        <input
                          type="text"
                          className="border border-gray-300 rounded-md p-1 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 disabled:bg-gray-100 w-full"
                          value={checkedTs3}
                          onChange={(e) => setCheckedTs3(e.target.value)}
                          disabled={!isEditable}
                        />
                        <div className="text-gray-700">
                          Wheel Temp. RH:{' '}
                          <span className="inline-flex items-center font-semibold">
                            {formatValue(selectedLog?.Ts4)}
                            {getTempStatusIcon(selectedLog?.Ts4Alarm, selectedLog?.Ts4Warning)}
                          </span>
                        </div>
                        <div className="text-gray-700">Checked Wheel Temp. RH:</div>
                        <input
                          type="text"
                          className="border border-gray-300 rounded-md p-1 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 disabled:bg-gray-100 w-full"
                          value={checkedTs4}
                          onChange={(e) => setCheckedTs4(e.target.value)}
                          disabled={!isEditable}
                        />
                      </>
                    )}
                    {noOfTempSensors >= 6 && (
                      <>
                        <div className="text-gray-700">
                          Brake Disc Temp. LH:{' '}
                          <span className="inline-flex items-center font-semibold">
                            {formatValue(selectedLog?.Ts5)}
                            {getTempStatusIcon(selectedLog?.Ts5Alarm, selectedLog?.Ts5Warning)}
                          </span>
                        </div>
                        <div className="text-gray-700">Checked Brake Disc Temp. LH:</div>
                        <input
                          type="text"
                          className="border border-gray-300 rounded-md p-1 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 disabled:bg-gray-100 w-full"
                          value={checkedTs5}
                          onChange={(e) => setCheckedTs5(e.target.value)}
                          disabled={!isEditable}
                        />
                        <div className="text-gray-700">
                          Brake Disc Temp. RH:{' '}
                          <span className="inline-flex items-center font-semibold">
                            {formatValue(selectedLog?.Ts6)}
                            {getTempStatusIcon(selectedLog?.Ts6Alarm, selectedLog?.Ts6Warning)}
                          </span>
                        </div>
                        <div className="text-gray-700">Checked Brake Disc Temp. RH:</div>
                        <input
                          type="text"
                          className="border border-gray-300 rounded-md p-1 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 disabled:bg-gray-100 w-full"
                          value={checkedTs6}
                          onChange={(e) => setCheckedTs6(e.target.value)}
                          disabled={!isEditable}
                        />
                      </>
                    )}
                  </div>
                  <div className="col-span-2 space-y-2">
                    <div>
                      <label className="font-semibold text-gray-700 block mb-1">1. Recommended Action:</label>
                      <textarea
                        className="border border-gray-300 rounded-md p-2 w-full h-16 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 disabled:bg-gray-100 resize-none"
                        value={recommendedAction}
                        onChange={(e) => setRecommendedAction(e.target.value)}
                        disabled={!isEditable}
                      />
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700 block mb-1">2. Action Taken:</label>
                      <textarea
                        className="border border-gray-300 rounded-md p-2 w-full h-16 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 disabled:bg-gray-100 resize-none"
                        value={actionTaken}
                        onChange={(e) => setActionTaken(e.target.value)}
                        disabled={!isEditable}
                      />
                    </div>
                    <div>
                      <label className="font-semibold text-gray-700 block mb-1">3. Category of Defect:</label>
                      <textarea
                        className="border border-gray-300 rounded-md p-2 w-full h-16 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 disabled:bg-gray-100 resize-none"
                        value={categoryOfDefect}
                        onChange={(e) => setCategoryOfDefect(e.target.value)}
                        disabled={!isEditable}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex justify-center items-center gap-4">
                  {!isEditable && (
                    <button
                      onClick={() => setIsEditable(true)}
                      className="bg-blue-100 p-2 rounded-full text-blue-600 hover:bg-blue-200 transition-colors"
                    >
                      <FaPencilAlt size={20} />
                    </button>
                  )}
                  {isEditable && (
                    <button
                      className={`bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 transition-colors flex items-center justify-center ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={handleUpdate}
                      disabled={isUpdating}
                    >
                      {isUpdating ? (
                        <div>
                          <svg className="animate-spin h-5 w-5 mr-2 text-white" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          Updating...
                        </div>
                      ) : (
                        'Update'
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};


export default TempLogTable;
